import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, type Company, type Currency, type Rebate, type RebateBand, type Terms } from "../lib/api";
import { todayInLondon } from "../lib/dates";
import { DOCUMENT_TYPES, openDownload, uploadFile, uploadProblem } from "../lib/upload";

const money = (amount: number, currency: Currency) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
const agreed = (d: string) => dateFmt.format(new Date(`${d}T12:00:00Z`));

/** Eddie's typical terms: 12 weeks, sliding 100 / 50 / 25%. */
const TYPICAL_BANDS: RebateBand[] = [
  { fromWeek: 1, toWeek: 4, refundPercent: 100 },
  { fromWeek: 5, toWeek: 8, refundPercent: 50 },
  { fromWeek: 9, toWeek: 12, refundPercent: 25 },
];

export function describeFee(t: Pick<Terms, "feeType" | "feePercent" | "feeFixed" | "currency">): string {
  return t.feeType === "percent" ? `${t.feePercent}%` : money(t.feeFixed ?? 0, t.currency);
}

export function describeRebate(r: Rebate): string {
  switch (r.kind) {
    case "none":
      return "No rebate";
    case "free_replacement":
      return `Free replacement within ${r.periodWeeks} weeks`;
    case "sliding_refund":
      return r.schedule.map((b) => `${b.refundPercent}% in weeks ${b.fromWeek}–${b.toWeek}`).join(", ");
  }
}

export function TermsSection({ company, onMarkedClient }: { company: Company; onMarkedClient: (c: Company) => void }) {
  const [current, setCurrent] = useState<Terms | null>(null);
  const [history, setHistory] = useState<Terms[]>([]);
  const [editing, setEditing] = useState(false);
  const load = useAction();
  const act = useAction();

  const reload = useCallback(
    () =>
      load.run(async () => {
        const t = await api.terms(company.id);
        setCurrent(t.current);
        setHistory(t.history);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [company.id],
  );
  useEffect(() => void reload(), [reload]);

  const markClient = () =>
    void act.run(async () => onMarkedClient((await api.saveClient(company.id, { status: "client" })).company));

  return (
    <section className="section" aria-labelledby="terms-h">
      <div className="section-head">
        <h2 id="terms-h" className="label">
          Terms &amp; fees
        </h2>
        {!editing && (
          <button type="button" className="link-btn" onClick={() => setEditing(true)}>
            {current ? "Record new terms" : "+ Record terms"}
          </button>
        )}
      </div>
      <ErrorNotice error={load.error ?? act.error} />

      {current && !editing && (
        <dl className="ledger">
          <div>
            <dt className="label">Fee</dt>
            <dd className="figure-xl">{describeFee(current)}</dd>
            <dd className="secondary">{current.feeType === "percent" ? "of first-year base salary" : "per placement"}</dd>
          </div>
          <div>
            <dt className="label">Payment</dt>
            <dd className="figure-xl">{current.paymentTermsDays}d</dd>
            <dd className="secondary">from invoice, {current.currency}</dd>
          </div>
          <div className="wide">
            <dt className="label">Rebate</dt>
            <dd>{describeRebate(current.rebate)}</dd>
          </div>
          <div className="wide">
            <dt className="label">Agreed</dt>
            <dd>
              <span className="figures">{agreed(current.agreedOn)}</span>
              {current.document && (
                <>
                  {" · "}
                  <button type="button" className="link-btn" onClick={() => void act.run(() => openDownload(current.document!.id))}>
                    Download {current.document.filename}
                  </button>
                </>
              )}
            </dd>
            {current.notes && <dd className="secondary prewrap">{current.notes}</dd>}
          </div>
        </dl>
      )}

      {current && !editing && company.client?.status === "lead" && (
        <p className="notice ok">
          Terms are agreed, but this company is still a lead.{" "}
          <button type="button" className="link-btn" disabled={act.pending} onClick={markClient}>
            Mark as client
          </button>
        </p>
      )}

      {!current && !editing && !load.pending && (
        <p className="empty-line">No terms recorded. Store the fee, rebate and signed T&amp;Cs here once agreed.</p>
      )}

      {editing && (
        <TermsForm
          companyId={company.id}
          previous={current}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void reload();
          }}
        />
      )}

      {history.length > 1 && !editing && (
        <details className="earlier">
          <summary className="label">Earlier versions · {history.length - 1}</summary>
          <ol className="index">
            {history.slice(1).map((t) => (
              <li key={t.id}>
                <span className="num figures">{t.agreedOn.slice(2, 7)}</span>
                <span className="main">
                  <span className="primary">
                    {describeFee(t)} · {t.paymentTermsDays} days
                  </span>
                  <span className="secondary">
                    {describeRebate(t.rebate)} · agreed {agreed(t.agreedOn)}
                  </span>
                </span>
                <span className="actions">
                  {t.document && (
                    <button type="button" className="link-btn" onClick={() => void act.run(() => openDownload(t.document!.id))}>
                      Document
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}

function TermsForm({
  companyId,
  previous,
  onCancel,
  onSaved,
}: {
  companyId: string;
  previous: Terms | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { pending, error, setError, run } = useAction();
  const [feeType, setFeeType] = useState<"percent" | "fixed">(previous?.feeType ?? "percent");
  const [feePercent, setFeePercent] = useState(String(previous?.feePercent ?? 20));
  const [feeFixed, setFeeFixed] = useState(previous?.feeFixed ? String(previous.feeFixed) : "");
  const [currency, setCurrency] = useState<Currency>(previous?.currency ?? "GBP");
  const [paymentDays, setPaymentDays] = useState(String(previous?.paymentTermsDays ?? 30));
  const [rebateKind, setRebateKind] = useState<Rebate["kind"]>(previous?.rebate.kind ?? "sliding_refund");
  const [replacementWeeks, setReplacementWeeks] = useState(
    String(previous?.rebate.kind === "free_replacement" ? previous.rebate.periodWeeks : 12),
  );
  const [bands, setBands] = useState<RebateBand[]>(
    previous?.rebate.kind === "sliding_refund" ? previous.rebate.schedule : TYPICAL_BANDS,
  );
  const [agreedOn, setAgreedOn] = useState(todayInLondon());
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");

  // Bands always run contiguously: each starts the week after the previous one ends.
  const setBand = (i: number, patch: Partial<Pick<RebateBand, "toWeek" | "refundPercent">>) =>
    setBands((bs) => reflow(bs.map((b, j) => (j === i ? { ...b, ...patch } : b))));
  const addBand = () =>
    setBands((bs) => {
      const last = bs.at(-1);
      const from = last ? last.toWeek + 1 : 1;
      return [...bs, { fromWeek: from, toWeek: Math.min(from + 3, 52), refundPercent: last ? Math.max(0, last.refundPercent / 2) : 100 }];
    });
  const removeBand = (i: number) => setBands((bs) => reflow(bs.filter((_, j) => j !== i)));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (file) {
      const problem = uploadProblem(file, DOCUMENT_TYPES);
      if (problem) return setError(problem);
    }
    void run(async () => {
      const documentFileId = file ? (await uploadFile(file)).id : null;
      const rebate: Rebate =
        rebateKind === "none"
          ? { kind: "none" }
          : rebateKind === "free_replacement"
            ? { kind: "free_replacement", periodWeeks: Number(replacementWeeks) }
            : { kind: "sliding_refund", periodWeeks: bands.at(-1)?.toWeek ?? 0, schedule: bands };
      await api.recordTerms(companyId, {
        feeType,
        feePercent: feeType === "percent" ? Number(feePercent) : undefined,
        feeFixed: feeType === "fixed" ? Number(feeFixed.replace(/[£€$,\s]/g, "")) : undefined,
        currency,
        paymentTermsDays: Number(paymentDays),
        rebate,
        agreedOn,
        documentFileId,
        notes: notes.trim(),
      });
      onSaved();
    });
  };

  return (
    <form className="form panel" onSubmit={submit} aria-label="Record terms">
      <p className="hint">{previous ? "Saving creates a new version; the current terms stay in the history." : ""}</p>
      <fieldset className="segmented">
        <legend className="label">Fee</legend>
        {(
          [
            ["percent", "% of salary"],
            ["fixed", "Fixed fee"],
          ] as const
        ).map(([v, label]) => (
          <label key={v}>
            <input type="radio" name="feeType" checked={feeType === v} onChange={() => setFeeType(v)} />
            <span>{label}</span>
          </label>
        ))}
      </fieldset>
      <div className="grid-3">
        {feeType === "percent" ? (
          <Field label="Fee %" inputMode="decimal" required value={feePercent} onChange={(e) => setFeePercent(e.target.value)} />
        ) : (
          <Field label="Fixed fee" inputMode="decimal" required value={feeFixed} onChange={(e) => setFeeFixed(e.target.value)} />
        )}
        <label className="field">
          <span className="label">Currency</span>
          <select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)}>
            <option value="GBP">GBP £</option>
            <option value="EUR">EUR €</option>
            <option value="USD">USD $</option>
          </select>
        </label>
        <Field
          label="Payment terms (days)"
          inputMode="numeric"
          required
          value={paymentDays}
          onChange={(e) => setPaymentDays(e.target.value)}
          hint={
            <span className="quick-dates">
              {[30, 45, 60].map((d) => (
                <button key={d} type="button" className="link-btn" onClick={() => setPaymentDays(String(d))}>
                  {d}
                </button>
              ))}
            </span>
          }
        />
      </div>

      <fieldset className="segmented">
        <legend className="label">Rebate if the candidate leaves early</legend>
        {(
          [
            ["sliding_refund", "Sliding refund"],
            ["free_replacement", "Free replacement"],
            ["none", "None"],
          ] as const
        ).map(([v, label]) => (
          <label key={v}>
            <input type="radio" name="rebate" checked={rebateKind === v} onChange={() => setRebateKind(v)} />
            <span>{label}</span>
          </label>
        ))}
      </fieldset>

      {rebateKind === "free_replacement" && (
        <div className="grid-3">
          <Field label="Within (weeks)" inputMode="numeric" value={replacementWeeks} onChange={(e) => setReplacementWeeks(e.target.value)} />
        </div>
      )}

      {rebateKind === "sliding_refund" && (
        <div className="bands">
          <table>
            <thead>
              <tr>
                <th className="label">Weeks</th>
                <th className="label">Refund</th>
                <th>
                  <span className="sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {bands.map((b, i) => (
                <tr key={i}>
                  <td className="figures">
                    {b.fromWeek} –{" "}
                    <input
                      aria-label={`Band ${i + 1} last week`}
                      inputMode="numeric"
                      value={b.toWeek}
                      onChange={(e) => setBand(i, { toWeek: Number(e.target.value) || b.fromWeek })}
                    />
                  </td>
                  <td className="figures">
                    <input
                      aria-label={`Band ${i + 1} refund percent`}
                      inputMode="decimal"
                      value={b.refundPercent}
                      onChange={(e) => setBand(i, { refundPercent: Number(e.target.value) || 0 })}
                    />
                    %
                  </td>
                  <td>
                    {bands.length > 1 && (
                      <button type="button" className="link-btn" onClick={() => removeBand(i)} aria-label={`Remove band ${i + 1}`}>
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="button-row">
            <button type="button" className="link-btn" onClick={addBand} disabled={(bands.at(-1)?.toWeek ?? 0) >= 52}>
              + Add band
            </button>
            <button type="button" className="link-btn" onClick={() => setBands(TYPICAL_BANDS)}>
              Use 100 / 50 / 25% over 12 weeks
            </button>
          </div>
        </div>
      )}

      <div className="grid-2">
        <Field label="Agreed on" type="date" required value={agreedOn} onChange={(e) => setAgreedOn(e.target.value)} />
        <label className="field">
          <span className="label">Signed T&amp;Cs (PDF or Word, up to 10 MB)</span>
          <input
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
      </div>
      <label className="field">
        <span className="label">Notes</span>
        <textarea rows={3} maxLength={5000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Exclusivity, retainer, special clauses…" />
      </label>

      <ErrorNotice error={error} />
      <div className="button-row">
        <button className="btn" disabled={pending}>
          {pending ? (file ? "Uploading…" : "Saving…") : "Save terms"}
        </button>
        <button type="button" className="link-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Re-chain bands so each starts the week after the previous ends. */
export function reflow(bands: RebateBand[]): RebateBand[] {
  let next = 1;
  return bands.map((b) => {
    const fromWeek = next;
    const toWeek = Math.min(52, Math.max(fromWeek, b.toWeek));
    next = toWeek + 1;
    return { ...b, fromWeek, toWeek };
  });
}
